"use client"

import {
	SidebarGroup,
	SidebarGroupContent,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
} from "@medme/ui"
import { Link, useRouterState } from "@tanstack/react-router"
import { ChevronDown } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { getLeadBusinessesAccess, useUserBusinesses } from "@/entities/business"
import {
	getMarketingNavVisibility,
	useMarketingContext,
} from "@/entities/marketing"
import { navigationConfig } from "../model/navigation"
import { isNavigationItemActive } from "../model/utils"

export function SidebarNavigation() {
	const { t } = useTranslation()
	// Use useRouterState with selector to reactively get current pathname
	const currentPath = useRouterState({
		select: (state) => state.location.pathname,
	})
	const {
		data: marketingContext,
		isLoading: isMarketingContextLoading,
		isError: isMarketingContextError,
		isFetched: isMarketingContextFetched,
	} = useMarketingContext()
	const { data: userBusinesses } = useUserBusinesses()
	const { canView: canViewLeads } = getLeadBusinessesAccess(
		userBusinesses?.businesses ?? [],
	)

	const marketingNavVisibility = getMarketingNavVisibility(marketingContext, {
		isLoading: isMarketingContextLoading || !isMarketingContextFetched,
		isError: isMarketingContextError,
		isFetched: isMarketingContextFetched,
	})

	const visibleNavigation = useMemo(
		() =>
			navigationConfig.flatMap((item) => {
				if (item.id === "marketing" && marketingNavVisibility !== "visible") {
					return []
				}
				if (item.id !== "journals") return [item]

				const children = item.children?.filter(
					(child) => child.id !== "leads" || canViewLeads,
				)
				return children?.length ? [{ ...item, children }] : []
			}),
		[canViewLeads, marketingNavVisibility],
	)
	const [expandedSections, setExpandedSections] = useState<Set<string>>(
		new Set(["analytics"]), // Default: analytics section expanded
	)

	// Automatically expand sections that contain active navigation items
	useEffect(() => {
		const sectionsToExpand = new Set<string>()
		for (const item of visibleNavigation) {
			if (item.children) {
				// Check if any child is active
				const hasActiveChild = item.children.some((child) =>
					isNavigationItemActive(child, currentPath),
				)
				if (hasActiveChild) {
					sectionsToExpand.add(item.id)
				}
			}
		}
		// Merge with existing expanded sections (preserve user's manual toggles)
		setExpandedSections((prev) => {
			const merged = new Set(prev)
			for (const sectionId of sectionsToExpand) {
				merged.add(sectionId)
			}
			return merged
		})
	}, [currentPath, visibleNavigation])

	const toggleSection = (sectionId: string) => {
		setExpandedSections((prev) => {
			const next = new Set(prev)
			if (next.has(sectionId)) {
				next.delete(sectionId)
			} else {
				next.add(sectionId)
			}
			return next
		})
	}

	return (
		<SidebarGroup>
			<SidebarGroupContent>
				<SidebarMenu>
					{visibleNavigation.map((item) => {
						const isExpanded = expandedSections.has(item.id)
						const hasChildren = item.children && item.children.length > 0

						return (
							<SidebarMenuItem key={item.id}>
								{hasChildren ? (
									<>
										<SidebarMenuButton
											onClick={() => toggleSection(item.id)}
											aria-expanded={isExpanded}
											aria-controls={`nav-section-${item.id}`}
										>
											{item.icon && <item.icon />}
											<span>{t(item.label)}</span>
											<ChevronDown
												className="ml-auto transition-transform"
												style={{
													transform: isExpanded ? "rotate(180deg)" : undefined,
												}}
											/>
										</SidebarMenuButton>
										{isExpanded && (
											<SidebarMenuSub id={`nav-section-${item.id}`}>
												{item.children?.map((child) => {
													const isActive = isNavigationItemActive(
														child,
														currentPath,
													)
													return (
														<SidebarMenuSubItem key={child.id}>
															<SidebarMenuSubButton asChild isActive={isActive}>
																<Link to={child.route || "#"}>
																	<span>{t(child.label)}</span>
																</Link>
															</SidebarMenuSubButton>
														</SidebarMenuSubItem>
													)
												})}
											</SidebarMenuSub>
										)}
									</>
								) : (
									<SidebarMenuButton
										asChild
										isActive={isNavigationItemActive(item, currentPath)}
									>
										<Link to={item.route || "#"}>
											{item.icon && <item.icon />}
											<span>{t(item.label)}</span>
										</Link>
									</SidebarMenuButton>
								)}
							</SidebarMenuItem>
						)
					})}
				</SidebarMenu>
			</SidebarGroupContent>
		</SidebarGroup>
	)
}
